import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { api, apiErr, fileUrl } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { RenderCard } from "@/components/RenderCard";
import { BomView } from "@/components/BomView";
import { ZoneDrawer } from "@/components/ZoneDrawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Camera, Upload, Loader2, Wand2, ArrowRight, ArrowLeft, Sparkles,
  Lightbulb, Home, Building, Lock, Send, CheckCircle2, Image as ImageIcon, FileText,
} from "lucide-react";

const ROOMS = {
  interior: ["Living", "Dormitor", "Bucătărie", "Baie", "Hol / Scări", "Birou", "Comercial / HoReCa"],
  exterior: ["Fațadă casă", "Fațadă clădire", "Grădină / Peisaj", "Terasă / Pergolă", "Alee / Acces", "Piscină"],
};
const OBJECTIVES = ["Iluminat ambiental", "Accent arhitectural", "Iluminat funcțional", "Efect decorativ", "Iluminat de siguranță"];
const ZONES = {
  interior: ["Contur tavan", "Nișă", "Spate mobilier", "Soclu / Plintă", "Rafturi / Bibliotecă", "Oglindă / Baie", "Blat bucătărie", "Trepte scări"],
  exterior: ["Contur fațadă", "Streașină", "Gard / Împrejmuire", "Trepte / Alee", "Terasă / Pergolă", "Piscină", "Copaci / Peisaj", "Intrare"],
};
const TEMPS = ["2700K (Warm)", "3000K (Warm White)", "4000K (Neutral)", "6000K (Cold)", "RGB / RGBW"];
const INTENSITIES = ["Discretă", "Medie", "Puternică"];

const SCENE_WIDTH_M = { interior: 4.5, exterior: 9 };
const estimateMeters = (box, spaceType, scale) => {
  const perNorm = scale || SCENE_WIDTH_M[spaceType] || 4.5;
  const runNorm = Math.max(box.w || 0, box.h || 0);
  return Math.max(1, Math.min(30, Math.round(runNorm * perNorm)));
};

export default function Studio() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const { t, lang } = useT();
  const locale = lang === "en" ? "en-GB" : "ro-RO";

  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [name, setName] = useState("Proiect nou");
  const [spaceType, setSpaceType] = useState("interior");
  const [room, setRoom] = useState("Living");
  const [objective, setObjective] = useState("Iluminat ambiental");

  const [zones, setZones] = useState([]);
  const [drawnZones, setDrawnZones] = useState([]);
  const [showDrawer, setShowDrawer] = useState(false);
  const [temp, setTemp] = useState("3000K (Warm White)");
  const [intensity, setIntensity] = useState("Medie");
  const [meters, setMeters] = useState(6);
  const [zoneMeters, setZoneMeters] = useState({});
  const [manualZones, setManualZones] = useState({});
  const [scaleMPerNorm, setScaleMPerNorm] = useState(null);
  const [fixtures, setFixtures] = useState(false);

  const [project, setProject] = useState(null);
  const [render, setRender] = useState(null);
  const [creating, setCreating] = useState(false);
  const [generating, setGenerating] = useState(false);

  const [selectedStyle, setSelectedStyle] = useState(null);
  const [offerOpen, setOfferOpen] = useState(false);
  const [offerMsg, setOfferMsg] = useState("");
  const [offerSent, setOfferSent] = useState(false);
  const [sentOfferId, setSentOfferId] = useState(null);

  const limited = user && user.render_limit != null;
  const remaining = limited ? Math.max(0, user.render_limit - (user.renders_used || 0)) : null;

  useEffect(() => {
    if (!drawnZones.length) return;
    setZoneMeters((prev) => {
      const next = { ...prev };
      for (const d of drawnZones) {
        if (!manualZones[d.label]) next[d.label] = estimateMeters(d, spaceType, scaleMPerNorm);
      }
      return next;
    });
  }, [drawnZones, spaceType, scaleMPerNorm, manualZones]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const toggleZone = (z) =>
    setZones((s) => (s.includes(z) ? s.filter((x) => x !== z) : [...s, z]));

  const createProject = async () => {
    if (!file) return toast.error(t("Încarcă o fotografie mai întâi."));
    setCreating(true);
    try {
      const fd = new FormData();
      fd.append("photo", file);
      fd.append("name", name);
      fd.append("space_type", spaceType);
      fd.append("room", room);
      fd.append("objective", objective);
      const { data } = await api.post("/projects", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProject(data);
      setStep(2);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setCreating(false);
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const selectedZones = Array.from(new Set([...zones, ...drawnZones.map((d) => d.label)]));
      const zoneMetersVal = (z) => zoneMeters[z] ?? 3;
      const zone_specs = selectedZones.map((z) => ({ zone: z, meters: zoneMetersVal(z) }));
      const totalMeters = selectedZones.length
        ? selectedZones.reduce((s, z) => s + zoneMetersVal(z), 0)
        : meters;
      const { data } = await api.post(`/projects/${project.id}/generate`, {
        objective, color_temp: temp, intensity,
        zones: selectedZones, meters: totalMeters, zone_specs, include_fixtures: fixtures,
      });
      setRender(data.render);
      setStep(3);
      await refresh();
      const ok = data.render.images.filter((i) => i.ok).length;
      toast.success(`${ok} ${t("randări generate!")}`);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setGenerating(false);
    }
  };

  const sendOffer = async () => {
    try {
      const { data } = await api.post("/offers", {
        project_id: project.id, render_id: render.id, style_key: selectedStyle, message: offerMsg, lang,
      });
      setSentOfferId(data?.id || null);
      setOfferSent(true);
      setOfferOpen(false);
      toast.success(t("Cererea de ofertă a fost trimisă echipei de vânzări!"));
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const downloadOffer = async () => {
    if (!sentOfferId) return;
    try {
      toast.message(t("Se pregătește PDF-ul…"));
      const res = await api.get(`/offers/${sentOfferId}/pdf?lang=${lang}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `oferta-klus-${String(sentOfferId).slice(0, 8)}.pdf`;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const reset = () => {
    setStep(1); setFile(null); setPreview(null); setProject(null); setRender(null);
    setZones([]); setDrawnZones([]); setShowDrawer(false); setSelectedStyle(null); setOfferSent(false); setSentOfferId(null); setOfferMsg("");
    setZoneMeters({}); setManualZones({}); setScaleMPerNorm(null);
  };

  const generateMore = () => { setRender(null); setSelectedStyle(null); setStep(2); };

  const Stepper = () => (
    <div className="flex items-center justify-center gap-2 mb-8">
      {[1, 2, 3].map((n) => (
        <React.Fragment key={n}>
          <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold border transition-colors ${
            step >= n ? "bg-primary border-primary text-white" : "border-slate-700 text-slate-500"
          }`}>{n}</div>
          {n < 3 && <div className={`w-10 h-0.5 ${step > n ? "bg-primary" : "bg-slate-700"}`} />}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-6">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-white flex items-center justify-center gap-2">
            <Sparkles className="w-7 h-7 text-primary" /> {t("Studio AI")}
          </h1>
          <p className="text-slate-400 text-sm mt-2">
            {limited ? `${t("Ai")} ${remaining} ${t("din")} ${user.render_limit} ${t("randări rămase.")}` : t("Randări nelimitate (cont intern).")}
          </p>
        </div>

        <Stepper />

        {/* STEP 1 */}
        {step === 1 && (
          <div className="space-y-3 animate-fade-up">
            <label className="block cursor-pointer">
              <input type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="photo-input" />
              <div className={`rounded-3xl border-2 border-dashed p-8 text-center transition-all ${
                preview ? "border-primary/60 bg-primary/5" : "border-slate-700 bg-card hover:border-primary/50"
              }`}>
                {preview ? (
                  <img src={preview} alt="preview" className="max-h-72 mx-auto rounded-2xl object-contain" data-testid="photo-preview" />
                ) : (
                  <div className="py-8">
                    <div className="w-16 h-16 rounded-2xl bg-primary/15 border border-primary/30 flex items-center justify-center mx-auto mb-4">
                      <Camera className="w-8 h-8 text-primary" />
                    </div>
                    <p className="text-white font-medium text-lg">{t("Fă o poză sau încarcă o imagine")}</p>
                    <p className="text-slate-400 text-sm mt-1">{t("Interior sau fațadă · JPG / PNG, max 12MB")}</p>
                    <div className="flex items-center justify-center gap-2 mt-4 text-xs text-slate-500">
                      <Upload className="w-4 h-4" /> {t("Apasă pentru a selecta")}
                    </div>
                  </div>
                )}
              </div>
            </label>

            <div className="grid grid-cols-2 gap-3">
              <label className="cursor-pointer" data-testid="photo-gallery-label">
                <input type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="photo-input-gallery" />
                <div className="flex items-center justify-center gap-2 h-11 rounded-xl border border-slate-700 bg-slate-900 text-sm text-slate-200 hover:border-primary/50 transition-colors">
                  <ImageIcon className="w-4 h-4 text-primary" /> {t("Alege din galerie")}
                </div>
              </label>
              <label className="cursor-pointer" data-testid="photo-camera-label">
                <input type="file" accept="image/*" capture="environment" onChange={onFile} className="hidden" data-testid="photo-input-camera" />
                <div className="flex items-center justify-center gap-2 h-11 rounded-xl border border-slate-700 bg-slate-900 text-sm text-slate-200 hover:border-primary/50 transition-colors">
                  <Camera className="w-4 h-4 text-primary" /> {t("Fă o poză")}
                </div>
              </label>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-slate-400">{t("Nume proiect")}</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="project-name"
                  className="mt-1.5 bg-slate-900 border-slate-700 h-12" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-400 mb-2 block">{t("Tip spațiu")}</Label>
                <div className="grid grid-cols-2 gap-2">
                  {[{ k: "interior", i: Home, l: "Interior" }, { k: "exterior", i: Building, l: "Exterior" }].map((o) => {
                    const Icon = o.i;
                    return (
                      <button key={o.k} type="button" onClick={() => { setSpaceType(o.k); setRoom(ROOMS[o.k][0]); }}
                        data-testid={`space-${o.k}`}
                        className={`flex items-center justify-center gap-2 h-12 rounded-xl border transition-all ${
                          spaceType === o.k ? "border-primary bg-primary/10 text-white" : "border-slate-700 text-slate-400 hover:border-slate-600"
                        }`}>
                        <Icon className="w-4 h-4" /> {t(o.l)}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-400">{t("Spațiu")}</Label>
                <Select value={room} onValueChange={setRoom}>
                  <SelectTrigger data-testid="room-select" className="mt-1.5 bg-slate-900 border-slate-700 h-12"><SelectValue>{t(room)}</SelectValue></SelectTrigger>
                  <SelectContent>{ROOMS[spaceType].map((r) => <SelectItem key={r} value={r}>{t(r)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-slate-400">{t("Obiectiv iluminat")}</Label>
                <Select value={objective} onValueChange={setObjective}>
                  <SelectTrigger data-testid="objective-select" className="mt-1.5 bg-slate-900 border-slate-700 h-12"><SelectValue>{t(objective)}</SelectValue></SelectTrigger>
                  <SelectContent>{OBJECTIVES.map((o) => <SelectItem key={o} value={o}>{t(o)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            <Button onClick={createProject} disabled={creating || !file} data-testid="step1-next"
              className="w-full h-13 py-3.5 bg-primary hover:bg-primary/90 text-white text-base">
              {creating ? <Loader2 className="w-5 h-5 animate-spin" /> : <>{t("Continuă")} <ArrowRight className="w-4 h-4 ml-1.5" /></>}
            </Button>
          </div>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <div className="space-y-6 animate-fade-up">
            {preview && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs uppercase tracking-widest text-slate-400">{t("Fotografie")}</Label>
                  <button type="button" onClick={() => setShowDrawer((s) => !s)} data-testid="toggle-zone-drawer"
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${showDrawer ? "border-primary bg-primary/15 text-white" : "border-slate-700 text-slate-400"}`}>
                    {showDrawer ? t("Ascunde desenare") : t("✏️ Desenează zone pe poză")}
                  </button>
                </div>
                {showDrawer ? (
                  <ZoneDrawer src={preview} onChange={setDrawnZones} onCalibrate={setScaleMPerNorm} />
                ) : (
                  <img src={preview} alt="" className="max-h-56 mx-auto rounded-2xl object-contain border border-slate-800" />
                )}
                {drawnZones.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {drawnZones.map((z, i) => (
                      <Badge key={i} className="bg-primary/15 text-primary border-0">
                        {t(z.label)} · ~{zoneMeters[z.label] ?? estimateMeters(z, spaceType, scaleMPerNorm)} m
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-400 mb-2 block flex items-center gap-1.5">
                <Lightbulb className="w-3.5 h-3.5" /> {t("Zone de iluminat (alege una sau mai multe)")}
              </Label>
              <div className="flex flex-wrap gap-2">
                {ZONES[spaceType].map((z) => (
                  <button key={z} type="button" onClick={() => toggleZone(z)} data-testid={`zone-${z}`}
                    className={`px-3 py-2 rounded-full text-sm border transition-all min-h-[40px] ${
                      zones.includes(z) ? "border-primary bg-primary/15 text-white" : "border-slate-700 text-slate-400 hover:border-slate-600"
                    }`}>{t(z)}</button>
                ))}
              </div>
              <p className="text-[11px] text-slate-500 mt-2">{t("Dacă nu alegi nimic, AI-ul detectează automat zonele potrivite.")}</p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-400">{t("Temperatură culoare")}</Label>
                <Select value={temp} onValueChange={setTemp}>
                  <SelectTrigger data-testid="temp-select" className="mt-1.5 bg-slate-900 border-slate-700 h-12"><SelectValue /></SelectTrigger>
                  <SelectContent>{TEMPS.map((tv) => <SelectItem key={tv} value={tv}>{tv}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-400">{t("Intensitate")}</Label>
                <Select value={intensity} onValueChange={setIntensity}>
                  <SelectTrigger data-testid="intensity-select" className="mt-1.5 bg-slate-900 border-slate-700 h-12"><SelectValue>{t(intensity)}</SelectValue></SelectTrigger>
                  <SelectContent>{INTENSITIES.map((tv) => <SelectItem key={tv} value={tv}>{t(tv)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            {(() => {
              const selectedZones = Array.from(new Set([...zones, ...drawnZones.map((d) => d.label)]));
              const drawnLabels = new Set(drawnZones.map((d) => d.label));
              const zm = (z) => zoneMeters[z] ?? 3;
              const setZm = (z, v) => {
                setZoneMeters((s) => ({ ...s, [z]: v }));
                setManualZones((m) => ({ ...m, [z]: true }));
              };
              if (selectedZones.length === 0) {
                return (
                  <div className="rounded-2xl bg-card border border-slate-800 p-5">
                    <div className="flex items-center justify-between mb-3">
                      <Label className="text-xs uppercase tracking-widest text-slate-400">{t("Lungime estimată traseu LED")}</Label>
                      <span className="font-mono text-accent font-semibold" data-testid="meters-value">{meters} m</span>
                    </div>
                    <Slider value={[meters]} min={1} max={30} step={1} onValueChange={(v) => setMeters(v[0])} data-testid="meters-slider" />
                    <p className="text-[11px] text-slate-500 mt-2">{t("Alege zone mai sus pentru a introduce lungimea separat pe fiecare. Altfel, AI-ul detectează automat.")}</p>
                  </div>
                );
              }
              const total = selectedZones.reduce((s, z) => s + zm(z), 0);
              return (
                <div className="rounded-2xl bg-card border border-slate-800 p-5" data-testid="zone-meters-panel">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-xs uppercase tracking-widest text-slate-400">{t("Lungime traseu LED pe zonă")}</Label>
                    <span className="font-mono text-accent font-semibold" data-testid="zone-meters-total">{t("Total")} {total} m</span>
                  </div>
                  <div className="space-y-4">
                    {selectedZones.map((z) => (
                      <div key={z} data-testid={`zone-meter-row-${z}`}>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-sm text-slate-200 flex items-center gap-1.5">
                            {t(z)}
                            {drawnLabels.has(z) && (
                              <span className="text-[10px] text-accent/80 border border-accent/30 rounded px-1">{t("sugerat din desen")}</span>
                            )}
                          </span>
                          <span className="font-mono text-xs text-accent" data-testid={`zone-meter-value-${z}`}>{zm(z)} m</span>
                        </div>
                        <Slider value={[zm(z)]} min={1} max={30} step={1}
                          onValueChange={(v) => setZm(z, v[0])} data-testid={`zone-meter-slider-${z}`} />
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-slate-500 mt-3">{t("Zonele desenate pe poză primesc o lungime sugerată automat — ajusteaz-o oricând. Devizul se calculează din suma reală pe zone.")}</p>
                </div>
              );
            })()}

            <div className="flex items-center justify-between rounded-2xl bg-card border border-slate-800 p-5">
              <div>
                <p className="text-white font-medium text-sm">{t("Include corpuri de iluminat KLUS")}</p>
                <p className="text-[11px] text-slate-500">{t("Adaugă sisteme/corpuri gata asamblate în deviz.")}</p>
              </div>
              <Switch checked={fixtures} onCheckedChange={setFixtures} data-testid="fixtures-switch" />
            </div>

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(1)} data-testid="step2-back"
                className="h-13 py-3.5 border-slate-700 text-slate-200 hover:bg-slate-800">
                <ArrowLeft className="w-4 h-4 mr-1.5" /> {t("Înapoi")}
              </Button>
              <Button onClick={generate} disabled={generating || (limited && remaining <= 0)} data-testid="generate-btn"
                className="flex-1 h-13 py-3.5 bg-primary hover:bg-primary/90 text-white text-base">
                {generating ? <><Loader2 className="w-5 h-5 animate-spin mr-2" /> {t("Se generează 3 randări…")}</> : <><Wand2 className="w-5 h-5 mr-2" /> {t("Generează 3 randări AI")}</>}
              </Button>
            </div>
            {limited && remaining <= 0 && (
              <p className="text-center text-sm text-amber-400 flex items-center justify-center gap-1.5">
                <Lock className="w-4 h-4" /> {t("Ai atins limita. Trimite o cerere de ofertă pentru a continua.")}
              </p>
            )}
            {generating && (
              <div className="grid sm:grid-cols-3 gap-4">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="aspect-[4/3] rounded-2xl bg-slate-800 animate-soft-pulse" style={{ animationDelay: `${i * 0.2}s` }} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* STEP 3 */}
        {step === 3 && render && (
          <div className="space-y-8 animate-fade-up">
            {render.analysis?.description && (
              <div className="rounded-2xl bg-gradient-to-r from-accent/10 to-transparent border border-accent/20 p-5">
                <p className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-accent mb-2">
                  <Sparkles className="w-3.5 h-3.5" /> {t("Analiză Vision AI")}
                </p>
                <p className="text-slate-200 text-sm leading-relaxed">{render.analysis.description}</p>
                {render.analysis.recommendations?.length > 0 && (
                  <ul className="mt-3 space-y-1">
                    {render.analysis.recommendations.map((r, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                        <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" /> {r}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <div>
              <h3 className="font-display text-xl font-semibold text-white mb-4">{t("3 concepte de iluminat")}</h3>
              <div className="grid sm:grid-cols-3 gap-5">
                {render.images.map((img) => (
                  <RenderCard key={img.style_key} render={img} originalPath={project?.photo_path}
                    selected={selectedStyle === img.style_key} onSelect={setSelectedStyle} />
                ))}
              </div>
            </div>

            <div>
              <h3 className="font-display text-xl font-semibold text-white mb-4 flex items-center gap-2">
                {t("Lista de materiale (BOM)")}
                <Badge className="bg-slate-800 text-slate-300 border-0 font-normal">{t("estimare")}</Badge>
              </h3>
              <BomView bom={render.bom} />
            </div>

            <div className="sticky bottom-20 lg:bottom-4 z-30">
              <div className="glass border border-slate-800 rounded-2xl p-4 flex flex-col sm:flex-row gap-3 items-center">
                <div className="flex-1 text-sm text-slate-300 text-center sm:text-left">
                  {offerSent ? (
                    <span className="text-green-400 flex items-center gap-1.5 justify-center sm:justify-start">
                      <CheckCircle2 className="w-4 h-4" /> {t("Cerere trimisă. Echipa KLUS te va contacta.")}
                    </span>
                  ) : (
                    <>{t("Mulțumit de rezultat? Trimite lista către echipa de vânzări pentru ofertă finală.")}</>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                  <Button variant="outline" onClick={reset} data-testid="change-photo-btn"
                    className="border-slate-700 text-slate-200 hover:bg-slate-800">{t("Schimbă poza")}</Button>
                  {(!limited || remaining > 0) && (
                    <Button variant="outline" onClick={generateMore} data-testid="generate-more-btn"
                      className="border-primary/50 text-primary hover:bg-primary/10">
                      <Wand2 className="w-4 h-4 mr-1.5" /> {t("Încă 3 randări")}
                    </Button>
                  )}
                  {offerSent ? (
                    <Button onClick={downloadOffer} disabled={!sentOfferId} data-testid="download-offer-pdf-btn"
                      className="flex-1 bg-primary hover:bg-primary/90 text-white">
                      <FileText className="w-4 h-4 mr-1.5" /> {t("Descarcă oferta PDF")}
                    </Button>
                  ) : (
                    <Button onClick={() => setOfferOpen(true)} data-testid="request-offer-btn"
                      className="flex-1 bg-primary hover:bg-primary/90 text-white">
                      <Send className="w-4 h-4 mr-1.5" /> {t("Solicită ofertă")}
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {offerSent && render.bom && (
              <div className="rounded-2xl bg-primary/5 border border-primary/30 p-5" data-testid="offer-summary-card">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                  <h3 className="text-white font-semibold">{t("Rezumatul ofertei tale")}</h3>
                </div>
                <div className="flex items-baseline justify-between border-b border-slate-800 pb-3 mb-3">
                  <span className="text-slate-400 text-sm">{t("Total estimativ")}</span>
                  <span className="text-2xl font-extrabold text-accent" data-testid="offer-summary-total">
                    {(render.bom.estimated_total || 0).toLocaleString(locale)} {render.bom.currency || "RON"}
                  </span>
                </div>
                {render.bom.zone_breakdown?.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs uppercase tracking-widest text-slate-500 mb-2">{t("Lungimi pe zonă")}</p>
                    <div className="space-y-1.5">
                      {render.bom.zone_breakdown.map((z, i) => (
                        <div key={i} className="flex justify-between text-sm" data-testid={`offer-summary-zone-${i}`}>
                          <span className="text-slate-300">{t(z.zone)}</span>
                          <span className="font-mono text-slate-200">{z.meters} m</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {render.bom.groups?.length > 0 && (
                  <div>
                    <p className="text-xs uppercase tracking-widest text-slate-500 mb-2">{t("Pe categorii")}</p>
                    <div className="space-y-1.5">
                      {render.bom.groups.map((g, i) => (
                        <div key={i} className="flex justify-between text-sm">
                          <span className="text-slate-300">{g.label}</span>
                          <span className="font-mono text-slate-200">{(g.subtotal || 0).toLocaleString(locale)} lei</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <p className="text-[11px] text-slate-500 mt-3">{render.bom.disclaimer}</p>
              </div>
            )}

            {limited && remaining <= 0 && !offerSent && (
              <div className="rounded-2xl bg-amber-500/10 border border-amber-500/30 p-5 text-center">
                <Lock className="w-6 h-6 text-amber-400 mx-auto mb-2" />
                <p className="text-white font-medium">{t("Ai folosit cele 9 randări incluse.")}</p>
                <p className="text-sm text-slate-400 mt-1">{t("Pentru mai multe variante, echipa noastră de vânzări îți stă la dispoziție.")}</p>
              </div>
            )}
          </div>
        )}
      </div>

      <Dialog open={offerOpen} onOpenChange={setOfferOpen}>
        <DialogContent className="bg-card border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white">{t("Solicită ofertă personalizată")}</DialogTitle>
            <DialogDescription className="text-slate-400">
              {t("Trimitem lista de materiale și randarea aleasă către consultanții KLUS.")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label className="text-xs uppercase tracking-widest text-slate-400">{t("Mesaj (opțional)")}</Label>
            <Textarea value={offerMsg} onChange={(e) => setOfferMsg(e.target.value)} data-testid="offer-message"
              placeholder={t("Detalii despre proiect, termene, buget…")} className="bg-slate-900 border-slate-700 min-h-[100px]" />
            {selectedStyle && <p className="text-xs text-slate-500">{t("Variantă selectată:")} <b className="text-slate-300">{selectedStyle}</b></p>}
          </div>
          <DialogFooter>
            <Button onClick={sendOffer} data-testid="offer-send" className="bg-primary hover:bg-primary/90 text-white w-full">
              <Send className="w-4 h-4 mr-1.5" /> {t("Trimite cererea")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
